import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { PwaLayer } from '../../pwa/index.js';
import { ListenProvider } from '../../screens/listen/ListenProvider.js';
import { GlobalListenBar } from '../../screens/listen/GlobalListenBar.js';
import { useGlobalListenBarActive } from '../../screens/listen/ListenContext.js';
import { installViewportHeightVar } from './viewportHeight.js';
import './app-shell.css';

// Device diagnostics (`?vpdebug=1`, `?echoprobe=1`): dev server, cabrix previews
// and staging builds only (`__DEV_TOOLS__`, vite.config.ts), like the dev panel.
// A store build must not carry hidden, URL-activated panels (App Review 2.3.1);
// the constant-folded `null` keeps their chunks out of the production bundle.
const ViewportDebug = __DEV_TOOLS__
  ? lazy(() => import('./ViewportDebug.js').then((m) => ({ default: m.ViewportDebug })))
  : null;
const EchoProbe = __DEV_TOOLS__
  ? lazy(() => import('./EchoProbe.js').then((m) => ({ default: m.EchoProbe })))
  : null;

interface AppShellProps {
  children: ReactNode;
}

/**
 * El frame, adentro del `ListenProvider` para poder preguntarle si hay sesión
 * de escucha viva: con una, las hojas de la app tienen que reservarle al
 * reproductor la franja donde se va a apoyar (`--glb-room`, ver app-shell.css).
 * Sin nada sonando el valor es 0 y ninguna hoja pierde un píxel de alto.
 *
 * La variable va en el `<html>` y no en el frame porque hay hojas portaleadas a
 * `<body>` (RadarSheet), que no heredarían de acá. Mismo patrón que `--vp-h`
 * (viewportHeight.ts).
 */
function Frame({ children }: { children: ReactNode }) {
  const listening = useGlobalListenBarActive();
  useEffect(() => {
    const root = document.documentElement;
    if (listening) root.style.setProperty('--glb-room', `${GLB_ROOM_PX}px`);
    else root.style.removeProperty('--glb-room');
    return () => {
      root.style.removeProperty('--glb-room');
    };
  }, [listening]);

  return (
    <div className="app-shell__frame">
      {children}
      {/* Mini-player global: flota sobre las rutas que no tienen control de
          "Escuchar" propio, y se apoya en la hoja abierta (ver GlobalListenBar). */}
      <GlobalListenBar />
    </div>
  );
}

/**
 * Lo que una hoja abierta le cede al reproductor: 68px de barra + 8 de aire +
 * 16 de respiro contra el borde de arriba. Si cambia el alto de `.mp-bar`,
 * cambia acá.
 */
const GLB_ROOM_PX = 92;

/**
 * Responsive, mobile-first shell.
 *
 * - Mobile (< 600px): the column fills the full viewport (`100dvh`),
 *   honoring `viewport-fit=cover` safe-area insets.
 * - Tablet (600–1023px): the column is centered and capped at
 *   `min(440px, 100vw)` (phone-style frame) over the void background.
 * - Desktop (≥ 1024px): full-bleed — each screen renders its own desktop
 *   layout (`.app.dt-app` with sidebar + multi-column) via `useIsDesktop`.
 *   The CSS breakpoint here MUST stay in sync with `DESKTOP_BREAKPOINT_PX`.
 *
 * The `.app` class carries the design tokens + flex column layout from
 * tokens.css / global.css; this component only owns the outer framing.
 */
export function AppShell({ children }: AppShellProps) {
  // `--vp-h` medido por JS: ver viewportHeight.ts (dvh stale tras reload en Android).
  useEffect(() => installViewportHeightVar(), []);
  return (
    <div className="app-shell">
      {/* ListenProvider vive por ENCIMA de las rutas: su <audio> único persiste
          al navegar /listen ↔ /conversation (mini-player + retorno al player
          conservan posición). Ver screens/listen/ListenProvider.tsx. */}
      <ListenProvider>
        <Frame>{children}</Frame>
      </ListenProvider>
      {/* Overlay PWA (instalación / offline). Fixed: vive fuera del frame. */}
      <PwaLayer />
      {/* Diagnóstico del viewport en device (opt-in `?vpdebug=1`). */}
      {ViewportDebug && (
        <Suspense fallback={null}>
          <ViewportDebug />
        </Suspense>
      )}
      {/* Banco de medición del eco del coach (opt-in `?echoprobe=1`). Va acá,
          y no bajo una ruta, para que corra también sin login: no usa la
          sesión de voz ni el backend. */}
      {EchoProbe && (
        <Suspense fallback={null}>
          <EchoProbe />
        </Suspense>
      )}
    </div>
  );
}
